import * as React from 'react';
import * as dateFormat from 'dateformat';
import Page from '../../components/Page';
import { RouteComponentProps } from 'react-router';
import { StockAdjustment } from '../../product';
import { ErrorFlash } from '../../components/Flash';
import { ProfileContext, ProfileManager } from '../../profile';
import { useRequest, getHeaders, useRequestAsync } from '../../api/client';
import Loader from '../../components/Loader';
import Section from '../../components/Section';
import { Link } from 'react-router-dom';
import { Text, NumberField } from '../../components/forms';

interface Props extends RouteComponentProps<any> {}

const productID = (props: RouteComponentProps<any>) => {
  return props.match.params.productId;
};

const variantID = (props: RouteComponentProps<any>) => {
  return props.match.params.variantId;
};

const getVariant = (props: Props, context: ProfileManager) => {
  const [variant, _] = useRequest(() => ({
    url: `/store/products/${productID(props)}/variants/${variantID(props)}`,
    method: 'GET',
    headers: getHeaders(context),
  }));

  return variant;
};

const getStock = (props: Props, context: ProfileManager) => {
  return useRequest(() => ({
    url: `/store/products/${productID(props)}/variants/${variantID(
      props,
    )}/stock`,
    method: 'GET',
    headers: getHeaders(context),
  }));
};

const create = (props: Props, context: ProfileManager) =>
  useRequestAsync((data: StockAdjustment) => ({
    url: `/store/products/${productID(props)}/variants/${variantID(
      props,
    )}/stock/new`,
    method: 'POST',
    body: JSON.stringify(data),
    headers: getHeaders(context),
  }));

export default function VariantStock(props: Props) {
  const context = React.useContext(ProfileContext);
  const variant = getVariant(props, context);
  const [stock, requestStock] = getStock(props, context);
  const [state, submit] = create(props, context);

  const [notes, setNotes] = React.useState<string>(null);
  const [quantity, setQuantity] = React.useState<number>(null);

  React.useEffect(() => {
    if (state.complete && !state.error) {
      requestStock();
    }
  }, [state]);

  if (state.loading || variant.loading || stock.loading) {
    return <Loader />;
  }

  const handleSubmit = () =>
    submit({
      notes,
      quantity,
    });

  return (
    <Page
      title="Manage Stock"
      subtitle={variant.data ? variant.data.name : 'Variant'}
    >
      <div className="flex justify-center ml-auto mr-auto">
        <Link
          to={`/products/${productID(props)}/variants/${variantID(props)}`}
          className="f5 link br-pill bg-near-white hover-bg-black-10 ph4 pv3 mh2 b black"
        >
          ← Back to {variant.data ? variant.data.name : 'Variant'}
        </Link>
      </div>
      {variant.error && <ErrorFlash error={variant.error} />}
      {state.error && <ErrorFlash error={state.error} />}
      {stock.error && <ErrorFlash error={stock.error} />}
      <Section title="Add Stock">
        <form onSubmit={handleSubmit}>
          <div className="flex flex-wrap items-center">
            <div className="flex-auto w-third-ns w-100 pr2">
              <NumberField
                label="Quantity"
                field={['quantity']}
                id="quantity"
                defaultValue={null}
                onValueChange={(_: any, newValue: number) =>
                  setQuantity(newValue)
                }
                placeholder="Enter quantity i.e. 100"
              />
              Current stock amount: {variant.data && variant.data.stock_count}
            </div>
            <div className="flex-auto w-third-ns w-100 pl2">
              <Text
                label="Notes"
                field={['notes']}
                id="notes"
                defaultValue={null}
                onValueChange={(_: any, newValue: string) => setNotes(newValue)}
                placeholder="Add a reference i.e. Stock ordered on 1/2/19"
              />
            </div>
            <div className="w-auto pl2">
              <button
                className="f5 link dim br-pill bg-white-10 ph4 pv3 mh2 dib gold bn"
                type="button"
                onClick={handleSubmit}
              >
                Add
              </button>
            </div>
          </div>
        </form>
      </Section>
      {stock.data && (
        <Section title="Stock Adjustments">
          {!stock.data.length && (
            <div className="pa2 pa4-ns bb b--white-10">
              No entries yet.
            </div>
          )}
          <div className="pa2 pa4-ns bb b--white-10 b tr">
            Current: <span className="gold">{variant.data && variant.data.stock_count}</span>
          </div>
          {stock.data.map((item: StockAdjustment) => (
            <div className="flex justify-stretch items-center pa2 pa3-ns ph4-ns bb b--white-10">
              <div className="f6 w-30 white-70">
                {dateFormat(item.created_at, 'dddd, mmmm dS, yyyy, h:MM:ss TT')}
              </div>
              <div className="mr4 f6 w-50">{item.notes}</div>
              <div className="pl3 w-20 gold b tr">{item.quantity}</div>
            </div>
          ))}
        </Section>
      )}
    </Page>
  );
}
